import { create } from 'zustand';
import { BirthRecord } from '@/types/dog';
import { birthRecordService, BirthRecordSearchFilter } from '@/services/firebase/birthRecordService';

/**
 * 出産記録管理ストアの状態型定義
 */
interface BirthRecordState {
  // 状態
  birthRecords: BirthRecord[];
  selectedBirthRecord: BirthRecord | null;
  loading: boolean;
  error: string | null;
  searchFilter: BirthRecordSearchFilter;

  // アクション
  setBirthRecords: (birthRecords: BirthRecord[]) => void;
  setSelectedBirthRecord: (birthRecord: BirthRecord | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSearchFilter: (filter: BirthRecordSearchFilter) => void;
  clearSearchFilter: () => void;

  // API連携アクション
  fetchBirthRecords: (userId: string) => Promise<void>;
  fetchBirthRecord: (recordId: string, userId: string) => Promise<void>;
  fetchBirthRecordsByMother: (motherId: string, userId: string) => Promise<void>;
  fetchBirthRecordsByFather: (fatherId: string, userId: string) => Promise<void>;
  searchBirthRecords: (userId: string) => Promise<void>;
  deleteBirthRecord: (recordId: string, userId: string) => Promise<void>;
}

/**
 * 出産記録管理ストア
 */
export const useBirthRecordStore = create<BirthRecordState>((set, get) => ({
  // 初期状態
  birthRecords: [],
  selectedBirthRecord: null,
  loading: false,
  error: null,
  searchFilter: {},

  // アクション
  setBirthRecords: (birthRecords) => set({ birthRecords }),

  setSelectedBirthRecord: (birthRecord) => set({ selectedBirthRecord: birthRecord }),

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  setSearchFilter: (filter) => set({ searchFilter: filter }),

  clearSearchFilter: () => set({ searchFilter: {} }),

  /**
   * 出産記録一覧取得
   */
  fetchBirthRecords: async (userId: string) => {
    set({ loading: true, error: null });

    try {
      const birthRecords = await birthRecordService.getBirthRecords(userId, 'birthDate', 'desc');
      set({ birthRecords, loading: false });
    } catch (error) {
      console.error('出産記録一覧取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : '出産記録一覧の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 出産記録詳細取得
   */
  fetchBirthRecord: async (recordId: string, userId: string) => {
    set({ loading: true, error: null });

    try {
      const birthRecord = await birthRecordService.getBirthRecord(recordId, userId);

      if (!birthRecord) {
        throw new Error('出産記録が見つかりません');
      }

      set({ selectedBirthRecord: birthRecord, loading: false });
    } catch (error) {
      console.error('出産記録詳細取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : '出産記録詳細の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * メス犬の出産履歴取得
   */
  fetchBirthRecordsByMother: async (motherId: string, userId: string) => {
    set({ loading: true, error: null });

    try {
      const birthRecords = await birthRecordService.getBirthRecordsByMother(motherId, userId);
      set({ birthRecords, loading: false });
    } catch (error) {
      console.error('メス犬の出産履歴取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : 'メス犬の出産履歴の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * オス犬の父犬記録取得
   */
  fetchBirthRecordsByFather: async (fatherId: string, userId: string) => {
    set({ loading: true, error: null });

    try {
      const birthRecords = await birthRecordService.getBirthRecordsByFather(fatherId, userId);
      set({ birthRecords, loading: false });
    } catch (error) {
      console.error('オス犬の父犬記録取得エラー:', error);
      set({
        error: error instanceof Error ? error.message : 'オス犬の父犬記録の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 出産記録検索
   */
  searchBirthRecords: async (userId: string) => {
    set({ loading: true, error: null });

    try {
      const { searchFilter } = get();
      const birthRecords = await birthRecordService.searchBirthRecords(userId, searchFilter);

      set({ birthRecords, loading: false });
    } catch (error) {
      console.error('出産記録検索エラー:', error);
      set({
        error: error instanceof Error ? error.message : '出産記録検索に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 出産記録削除
   */
  deleteBirthRecord: async (recordId: string, userId: string) => {
    set({ loading: true, error: null });

    try {
      await birthRecordService.deleteBirthRecord(recordId, userId);

      // 一覧から削除した記録を除外
      const { birthRecords } = get();
      const updatedRecords = birthRecords.filter((record) => record.id !== recordId);

      set({ birthRecords: updatedRecords, selectedBirthRecord: null, loading: false });
    } catch (error) {
      console.error('出産記録削除エラー:', error);
      set({
        error: error instanceof Error ? error.message : '出産記録の削除に失敗しました',
        loading: false,
      });
    }
  },
}));
